import {
  computeLineSubtotal,
  formatMoney,
  QUOTE_LINE_COMMENT_MAX_LENGTH,
  type Currency,
  type QuoteModeValue,
} from '@devizy/shared';
import { ArrowCounterClockwise, DotsThreeVertical, Trash } from '@phosphor-icons/react/dist/ssr';
import * as React from 'react';

import { cn } from '../../lib/cn';
import { Badge } from '../badge';
import { Button } from '../button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu';
import { Input } from '../input';
import { Textarea } from '../textarea';

import { computeAdjustmentIndicator } from './adjustment-indicator';
import { DiscrepancyBadge } from './discrepancy-badge';

import type { QuoteViewExpectation, QuoteViewLabels, QuoteViewLine, QuoteViewMode } from './types';

const JUSTIFICATION_MAX_LENGTH = 200;

export interface QuoteLineProps {
  mode: QuoteViewMode;
  quoteMode: QuoteModeValue;
  line: QuoteViewLine;
  expectation: QuoteViewExpectation | null;
  currency: Currency;
  labels: QuoteViewLabels;
  onChange?: (patch: Partial<QuoteViewLine>) => void;
  onRemove?: () => void;
}

export function QuoteLine({
  mode,
  quoteMode,
  line,
  expectation,
  currency,
  labels,
  onChange,
  onRemove,
}: QuoteLineProps): React.ReactElement {
  const isEdit = mode === 'bidder-edit';
  const readOnly = !isEdit;
  const subtotal =
    line.subtotal !== null
      ? line.subtotal
      : computeLineSubtotal({
          mode: line.mode,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          adjustment: line.adjustment,
          isProposedForDeletion: line.isProposedForDeletion,
        }).subtotal;

  const isModified =
    expectation !== null &&
    (line.bidderTitle.trim() !== expectation.title.trim() ||
      line.bidderDescription.trim() !== expectation.description.trim());

  const isTimeBased = quoteMode === 'TIME_BASED_DAY' || quoteMode === 'TIME_BASED_HOUR';

  const adjustmentIndicator = computeAdjustmentIndicator(
    line.quantity,
    line.unitPrice,
    line.adjustment,
  );

  const justificationMissing = (line.additionJustification ?? '').trim().length === 0;
  const deletionReasonMissing = (line.deletionReason ?? '').trim().length === 0;
  const commentRequired = isEdit && isModified && !line.isProposedForDeletion;
  const commentMissing = commentRequired && (line.bidderComment ?? '').trim().length === 0;

  return (
    <div
      className={cn(
        'border-input bg-card relative flex flex-col gap-3 rounded-lg border border-l-4 p-3',
        line.discrepancyType === 'ADDITION' && 'border-l-success',
        line.discrepancyType === 'MODIFICATION' && 'border-l-warning',
        line.discrepancyType === 'DELETION_PROPOSED' && 'border-l-destructive',
        line.isProposedForDeletion && 'bg-muted/60 opacity-70',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <DiscrepancyBadge
            type={line.discrepancyType}
            label={labels.discrepancy[line.discrepancyType]}
          />
          {mode === 'project-tasks' && line.taskStatus ? (
            <Badge variant="outline">{line.taskStatus}</Badge>
          ) : null}
        </div>
        {isEdit && expectation ? (
          <div className="flex items-center gap-1">
            {line.isProposedForDeletion ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onChange?.({ isProposedForDeletion: false, deletionReason: null })}
                data-testid="quote-line-restore"
              >
                <ArrowCounterClockwise weight="bold" className="mr-1.5 h-4 w-4" />
                {labels.restoreLine}
              </Button>
            ) : (
              <>
                {isModified ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onChange?.({
                        bidderTitle: expectation.title,
                        bidderDescription: expectation.description,
                      })
                    }
                    data-testid="quote-line-revert"
                  >
                    <ArrowCounterClockwise weight="bold" className="mr-1.5 h-4 w-4" />
                    {labels.revertToRequester}
                  </Button>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={labels.lineMenuAria}
                      data-testid="quote-line-menu"
                    >
                      <DotsThreeVertical weight="bold" className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() =>
                        onChange?.({
                          isProposedForDeletion: true,
                          deletionReason: line.deletionReason ?? '',
                        })
                      }
                      data-testid="quote-line-mark-out-of-scope"
                    >
                      <Trash weight="bold" className="mr-2 h-4 w-4" />
                      {labels.markOutOfScope}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        ) : null}
        {isEdit && !expectation && line.expectationId === null && onRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={labels.removeAdditionAria}
            onClick={onRemove}
          >
            <Trash weight="bold" className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      {readOnly ? (
        <div className="flex flex-col gap-1">
          <p
            className={cn(
              'text-sm font-medium',
              line.isProposedForDeletion && 'line-through',
              isModified && expectation && 'text-warning',
            )}
          >
            {line.bidderTitle}
          </p>
          {line.bidderDescription.trim().length > 0 ? (
            <p className="text-muted-foreground text-sm whitespace-pre-wrap">
              {line.bidderDescription}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Input
            value={line.bidderTitle}
            onChange={(e) => onChange?.({ bidderTitle: e.target.value })}
            placeholder={labels.lineTitle}
            aria-label={labels.lineTitle}
            className={cn(
              'font-medium',
              line.isProposedForDeletion && 'line-through',
              isModified && expectation && 'text-warning',
            )}
          />
          <Textarea
            value={line.bidderDescription}
            onChange={(e) => onChange?.({ bidderDescription: e.target.value })}
            placeholder={labels.lineDescription}
            aria-label={labels.lineDescription}
            rows={2}
          />
        </div>
      )}

      {readOnly && isTimeBased && !line.isProposedForDeletion ? (
        <p className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
          <span>
            {line.quantity ?? '—'}{' '}
            {(quoteMode === 'TIME_BASED_DAY' ? labels.days : labels.hours).toLowerCase()} ×{' '}
            {line.unitPrice !== null && line.unitPrice !== undefined
              ? formatMoney(line.unitPrice, currency)
              : '—'}
            {line.adjustment
              ? ` (${labels.adjustment} ${formatMoney(line.adjustment, currency)})`
              : ''}
          </span>
          {adjustmentIndicator ? (
            <Badge
              variant="outline"
              className={cn(
                'w-fit',
                adjustmentIndicator.direction === 'reduction'
                  ? 'border-warning/30 bg-warning-tint text-warning-strong'
                  : 'border-destructive/25 bg-destructive-tint text-destructive-strong',
              )}
            >
              {adjustmentIndicator.direction === 'reduction' ? '−' : '+'}
              {adjustmentIndicator.percent} %
            </Badge>
          ) : null}
        </p>
      ) : null}

      {!readOnly && isTimeBased && !line.isProposedForDeletion ? (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <label className="text-muted-foreground flex flex-col gap-1 text-xs">
            <span>{quoteMode === 'TIME_BASED_DAY' ? labels.days : labels.hours}</span>
            <Input
              type="number"
              min={0}
              step={0.25}
              value={line.quantity ?? ''}
              onChange={(e) =>
                onChange?.({ quantity: e.target.value === '' ? null : Number(e.target.value) })
              }
              aria-label={labels.quantityAria}
            />
          </label>
          <div className="text-muted-foreground flex flex-col gap-1 text-xs">
            <span>
              {labels.calculatedPrice} ({currency})
            </span>
            {line.unitPrice !== null && line.unitPrice !== undefined ? (
              <>
                <output
                  className="border-input bg-muted/40 text-foreground flex h-9 items-center rounded-md border px-3 text-sm"
                  data-testid="quote-line-calculated-price"
                >
                  {formatMoney((line.quantity ?? 0) * line.unitPrice, currency)}
                </output>
                <span>
                  {formatMoney(line.unitPrice, currency)} × {line.quantity ?? 0}{' '}
                  {(quoteMode === 'TIME_BASED_DAY' ? labels.days : labels.hours).toLowerCase()}
                </span>
              </>
            ) : (
              <output
                className="border-input bg-muted/40 text-warning flex min-h-9 items-center rounded-md border px-3 py-1 text-xs"
                data-testid="quote-line-rate-missing"
              >
                {labels.rateMissing}
              </output>
            )}
          </div>
          <label className="text-muted-foreground flex flex-col gap-1 text-xs">
            <span>
              {labels.adjustment} ({currency})
            </span>
            <Input
              type="number"
              step={0.01}
              value={line.adjustment ?? ''}
              onChange={(e) =>
                onChange?.({ adjustment: e.target.value === '' ? null : Number(e.target.value) })
              }
              aria-label={labels.adjustment}
            />
            {adjustmentIndicator ? (
              <Badge
                variant="outline"
                className={cn(
                  'w-fit',
                  adjustmentIndicator.direction === 'reduction'
                    ? 'border-warning/30 bg-warning-tint text-warning-strong'
                    : 'border-destructive/25 bg-destructive-tint text-destructive-strong',
                )}
              >
                {adjustmentIndicator.direction === 'reduction' ? '−' : '+'}
                {adjustmentIndicator.percent} %
              </Badge>
            ) : null}
          </label>
        </div>
      ) : null}

      {!readOnly && quoteMode === 'PER_LINE' && !line.isProposedForDeletion ? (
        <label className="text-muted-foreground flex flex-col gap-1 text-xs">
          <span>
            {labels.linePrice} ({currency})
          </span>
          <Input
            type="number"
            min={0}
            step={0.01}
            value={line.unitPrice ?? ''}
            onChange={(e) =>
              onChange?.({ unitPrice: e.target.value === '' ? null : Number(e.target.value) })
            }
            aria-label={labels.linePrice}
          />
        </label>
      ) : null}

      {!readOnly && quoteMode === 'FIXED' && !line.isProposedForDeletion ? (
        <label className="text-muted-foreground flex flex-col gap-1 text-xs">
          <span>
            {labels.fixedShare} ({currency})
          </span>
          <Input
            type="number"
            min={0}
            step={0.01}
            value={line.unitPrice ?? ''}
            onChange={(e) =>
              onChange?.({ unitPrice: e.target.value === '' ? null : Number(e.target.value) })
            }
            aria-label={labels.fixedShare}
          />
        </label>
      ) : null}

      {line.discrepancyDecision === 'REFUSED' || line.discrepancyDecision === 'ACCEPTED' ? (
        <div
          className={
            line.discrepancyDecision === 'REFUSED'
              ? 'border-destructive/30 bg-destructive-tint/40 space-y-1 rounded-md border px-2.5 py-2'
              : 'border-success/30 bg-success-tint/40 space-y-1 rounded-md border px-2.5 py-2'
          }
          data-testid="quote-line-decision"
          data-decision={line.discrepancyDecision}
        >
          <p className="text-xs font-medium">
            {line.discrepancyDecision === 'REFUSED'
              ? labels.discrepancyRefused
              : labels.discrepancyAccepted}
          </p>
          {(line.requesterRefusalComment ?? '').trim().length > 0 ? (
            <p className="text-xs">
              <span className="text-muted-foreground font-medium">
                {labels.requesterCommentLabel} :
              </span>{' '}
              <span className="break-words whitespace-pre-wrap">
                {line.requesterRefusalComment}
              </span>
            </p>
          ) : null}
        </div>
      ) : null}

      {readOnly && line.isProposedForDeletion && (line.deletionReason ?? '').trim().length > 0 ? (
        <p className="text-xs">
          <span className="text-muted-foreground font-medium">{labels.deletionReason} :</span>{' '}
          <span className="break-words whitespace-pre-wrap">{line.deletionReason}</span>
        </p>
      ) : null}

      {readOnly &&
      line.discrepancyType === 'ADDITION' &&
      (line.additionJustification ?? '').trim().length > 0 ? (
        <p className="text-xs">
          <span className="text-muted-foreground font-medium">
            {labels.additionJustification} :
          </span>{' '}
          <span className="break-words whitespace-pre-wrap">{line.additionJustification}</span>
        </p>
      ) : null}

      {readOnly && (line.bidderComment ?? '').trim().length > 0 ? (
        <p className="text-xs">
          <span className="text-muted-foreground font-medium">{labels.comment} :</span>{' '}
          <span className="break-words whitespace-pre-wrap">{line.bidderComment}</span>
        </p>
      ) : null}

      {!readOnly && line.isProposedForDeletion ? (
        <label className="text-muted-foreground flex flex-col gap-1 text-xs">
          <span>
            {labels.deletionReason}
            <span aria-hidden className="text-destructive ml-0.5">
              *
            </span>
          </span>
          <Textarea
            value={line.deletionReason ?? ''}
            onChange={(e) => onChange?.({ deletionReason: e.target.value })}
            required
            maxLength={JUSTIFICATION_MAX_LENGTH}
            rows={2}
            placeholder={labels.deletionReasonPlaceholder}
            aria-invalid={deletionReasonMissing}
            className={cn(
              deletionReasonMissing && 'border-destructive focus-visible:ring-destructive',
            )}
            data-testid="quote-line-deletion-reason"
          />
          <span className="flex items-center justify-between">
            {deletionReasonMissing ? (
              <span className="text-destructive">{labels.deletionReasonRequired}</span>
            ) : (
              <span />
            )}
            <span>{labels.justificationCounter((line.deletionReason ?? '').trim().length)}</span>
          </span>
        </label>
      ) : null}

      {!readOnly && line.discrepancyType === 'ADDITION' && !line.isProposedForDeletion ? (
        <label className="text-muted-foreground flex flex-col gap-1 text-xs">
          <span>
            {labels.additionJustification}
            <span aria-hidden className="text-destructive ml-0.5">
              *
            </span>
          </span>
          <Textarea
            value={line.additionJustification ?? ''}
            onChange={(e) => onChange?.({ additionJustification: e.target.value })}
            required
            maxLength={JUSTIFICATION_MAX_LENGTH}
            rows={2}
            placeholder={labels.additionJustificationPlaceholder}
            aria-invalid={justificationMissing}
            className={cn(
              justificationMissing && 'border-destructive focus-visible:ring-destructive',
            )}
          />
          <span className="flex items-center justify-between">
            {justificationMissing ? (
              <span className="text-destructive">{labels.additionJustificationRequired}</span>
            ) : (
              <span />
            )}
            <span>
              {labels.justificationCounter((line.additionJustification ?? '').trim().length)}
            </span>
          </span>
        </label>
      ) : null}

      {!readOnly ? (
        <label className="text-muted-foreground flex flex-col gap-1 text-xs">
          <span>
            {commentRequired ? labels.commentRequiredLabel : labels.comment}
            {commentRequired ? (
              <span aria-hidden className="text-destructive ml-0.5">
                *
              </span>
            ) : null}
          </span>
          <Textarea
            value={line.bidderComment ?? ''}
            onChange={(e) =>
              onChange?.({
                bidderComment: e.target.value.slice(0, QUOTE_LINE_COMMENT_MAX_LENGTH),
              })
            }
            required={commentRequired}
            maxLength={QUOTE_LINE_COMMENT_MAX_LENGTH}
            rows={2}
            placeholder={
              commentRequired ? labels.commentRequiredPlaceholder : labels.commentPlaceholder
            }
            aria-invalid={commentMissing}
            className={cn(commentMissing && 'border-destructive focus-visible:ring-destructive')}
            data-testid="quote-line-comment"
          />
          <span className="flex items-center justify-between">
            {commentMissing ? (
              <span className="text-destructive">{labels.commentRequired}</span>
            ) : (
              <span />
            )}
            <span data-testid="quote-line-comment-counter">
              {labels.commentCounter((line.bidderComment ?? '').trim().length)}
            </span>
          </span>
        </label>
      ) : null}

      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {line.isPricingComplete
            ? `${labels.subtotal} : ${formatMoney(subtotal, currency)}`
            : labels.priceIncomplete}
        </span>
        <span className="text-muted-foreground">#{line.order + 1}</span>
      </div>
    </div>
  );
}
